import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { backendService } from '../../services/backendService'
import { AdminSection } from './index'
import logoShort from '../../images/Logo Short.jpg'
import './AdminDashboard.css'

interface AdminSidebarProps {
  activeSection: AdminSection
  onSectionChange: (section: AdminSection) => void
  onSwitchToCAD?: () => void
}

interface MenuItem {
  id: AdminSection
  label: string
  icon: string
  description: string
}

const menuItems: MenuItem[] = [
  {
    id: 'register',
    label: 'Register Doctor',
    icon: '👨‍⚕️',
    description: 'Add new doctors'
  },
  {
    id: 'register-farm',
    label: 'Register Farm',
    icon: '🏭',
    description: 'Add new print farms'
  },
  {
    id: 'balance',
    label: 'Manage Balance',
    icon: '💰',
    description: 'Token management'
  },
  {
    id: 'doctor-ai-tokens',
    label: 'User AI Tokens',
    icon: '🤖',
    description: 'AI token management'
  },
  {
    id: 'list',
    label: 'Doctor List',
    icon: '📋',
    description: 'View all doctors'
  },
  {
    id: 'farm-list',
    label: 'Farm List',
    icon: '🏢',
    description: 'View all farms'
  },
  {
    id: 'farm-ai-tokens',
    label: 'Farm AI Tokens',
    icon: '🤖',
    description: 'Manage AI token balances'
  },
  {
    id: 'manage-presets',
    label: 'Manage Presets',
    icon: '📦',
    description: 'Upload presets for farms and doctors'
  }
]

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeSection,
  onSectionChange,
  onSwitchToCAD
}) => {
  const { doctorName, logout } = useAuth()
  const [tokenBalance, setTokenBalance] = useState<number | null>(null)
  const [aiTokenBalance, setAiTokenBalance] = useState<number | null>(null)
  const [loadingTokens, setLoadingTokens] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function fetchBalances() {
      try {
        const [tokens, aiTokens] = await Promise.allSettled([
          backendService.getTokenBalance(),
          backendService.getMyAiTokenBalance(),
        ])
        if (cancelled) return
        if (tokens.status === 'fulfilled') setTokenBalance(tokens.value.balance)
        if (aiTokens.status === 'fulfilled') setAiTokenBalance(aiTokens.value.balance)
      } catch {
        // silently fail — balances are non-critical
      } finally {
        if (!cancelled) setLoadingTokens(false)
      }
    }
    fetchBalances()
    return () => { cancelled = true }
  }, [])

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout()
    }
  }

  return (
    <div className="admin-sidebar">
      {/* Header */}
      <div className="admin-sidebar-header">
        <div className="admin-logo">
          <img src={logoShort} alt="Girasoles" className="admin-logo-icon" />
          <div className="admin-logo-text">
            <h2>Girasoles</h2>
            <p>Admin Panel</p>
          </div>
        </div>
      </div>

      {/* User Info */}
      <div className="admin-user-info">
        <div className="admin-user-avatar">
          <span>👤</span>
        </div>
        <div className="admin-user-details">
          <p className="admin-user-name">{doctorName || 'Admin'}</p>
          <p className="admin-user-role">Administrator</p>
        </div>
      </div>

      {/* Token Balances */}
      {!loadingTokens && (
        <div className="admin-sidebar-tokens">
          <div className="admin-token-card">
            <span className="admin-token-label">Tokens</span>
            <span className="admin-token-value">{tokenBalance !== null ? tokenBalance.toLocaleString() : '—'}</span>
          </div>
          <div className="admin-token-card">
            <span className="admin-token-label">AI Tokens</span>
            <span className="admin-token-value">{aiTokenBalance !== null ? aiTokenBalance.toLocaleString() : '—'}</span>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="admin-nav">
        <ul className="admin-nav-list">
          {menuItems.map((item) => (
            <li key={item.id}>
              <button
                className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => onSectionChange(item.id)}
                aria-current={activeSection === item.id ? 'page' : undefined}
              >
                <span className="admin-nav-icon">{item.icon}</span>
                <div className="admin-nav-text">
                  <span className="admin-nav-label">{item.label}</span>
                  <span className="admin-nav-description">{item.description}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="admin-sidebar-footer">
        {onSwitchToCAD && (
          <button
            className="admin-switch-button"
            onClick={onSwitchToCAD}
            title="Switch to CAD Workbench"
          >
            <span className="admin-switch-icon">🔧</span>
            <span>CAD Workbench</span>
          </button>
        )}
        <button
          className="admin-logout-button"
          onClick={handleLogout}
        >
          <span className="admin-logout-icon">🚪</span>
          <span>Logout</span>
        </button>
      </div>
    </div>
  )
}

